import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, ShieldCheck, Sparkles } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import FavoriteButton from "@/components/marketplace/FavoriteButton";

export default function ProviderCard({ provider, index = 0 }) {
  const verified = provider.verifications?.gcw_verified?.status === "approved";
  const type = provider.provider_type || "local";
  const isDigital = type === "digital";
  const isHybrid = type === "hybrid";

  const initials = (provider.business_name || provider.display_name || "GC")
    .split(" ")
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("");

  return (
    <div className="relative">
      <div className="absolute top-3 right-3 z-10">
        <FavoriteButton kind="provider" targetId={provider.id} initial={!!provider.is_favorite} />
      </div>
      <Link
        to={`/providers/${provider.slug || provider.id}`}
        data-testid={`provider-card-${provider.slug || provider.id}`}
        className="group rounded-2xl bg-white border border-slate-200 p-6 flex flex-col hover:border-[#165a71]/40 hover:shadow-[0_20px_40px_rgba(7,27,34,0.08)] hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-300 animate-fade-up"
        style={{ animationDelay: `${index * 40}ms` }}
      >
      <div className="flex items-start gap-4">
        <Avatar className="h-14 w-14 rounded-2xl ring-2 ring-[#165a71]/10">
          <AvatarImage src={provider.profile_image || undefined} alt={provider.display_name} />
          <AvatarFallback className="rounded-2xl bg-[#165a71] text-white font-display">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div translate="no" className="font-display text-base font-semibold text-[#071b22] truncate">
              {provider.business_name || provider.display_name}
            </div>
            {provider.is_featured && (
              <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" aria-label="Featured" />
            )}
          </div>
          <div translate={provider.tagline ? "no" : undefined} className="text-xs text-[#252A34]/60 mt-0.5 truncate">
            {provider.tagline || `${provider.years_experience || 0}+ yrs · Gulf Coast`}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
              <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />
              {(provider.rating_avg || 0).toFixed(1)}
              <span className="text-[#252A34]/50 ml-0.5">({provider.rating_count || 0})</span>
            </span>
            <span className="text-[#252A34]/40">·</span>
            <span className="text-[#252A34]/70">{provider.completed_jobs || 0} jobs</span>
            {provider.remote_available && (
              <>
                <span className="text-[#252A34]/40">·</span>
                <span className="text-[#252A34]/70">Remote OK</span>
              </>
            )}
          </div>
        </div>
      </div>

      {provider.short_intro && (
        <p className="mt-4 text-sm text-[#252A34]/80 leading-relaxed line-clamp-2">
          {provider.short_intro}
        </p>
      )}

      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge
            variant="outline"
            className={`rounded-full border ${
              isDigital
                ? "border-[#165a71]/30 bg-[#165a71]/5 text-[#165a71]"
                : isHybrid
                ? "border-amber-500/30 bg-amber-50 text-amber-700"
                : "border-slate-200 bg-slate-50 text-[#252A34]/80"
            }`}
          >
            {isDigital ? "Digital" : isHybrid ? "Hybrid" : "Local"}
          </Badge>
          {provider.provider_variant && provider.provider_variant !== "individual" && (
            <Badge variant="outline" className="rounded-full border-slate-200 bg-slate-50 text-[#252A34]/80 capitalize">
              {provider.provider_variant}
            </Badge>
          )}
        </div>
        {verified && (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
            <ShieldCheck className="h-3.5 w-3.5" /> Verified
          </span>
        )}
      </div>
    </Link>
    </div>
  );
}
